import { useEffect, useRef } from "react";
import { getToken } from "@/lib/api";

// Canal temps réel d'UNE publication (/api/ws/post/:id) partagé par tous les écrans ouverts dessus
// (feuille de commentaires, visionneuse plein écran, théâtre PC) : un seul WebSocket par publication,
// compté par abonné, fermé quand le dernier se désabonne, reconnecté tant qu'il reste des abonnés.
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
const channels = new Map(); // postId -> { ws, subs:Set<fn>, timer, attempt, closed }

const connect = (ch, postId) => {
  if (ch.closed) return;
  let ws;
  try { ws = new WebSocket(`${WS_BASE}/api/ws/post/${postId}?token=${getToken() || ""}`); } catch { return; }
  ch.ws = ws;
  ws.onopen = () => { ch.attempt = 0; };
  ws.onmessage = (evt) => {
    let msg;
    try { msg = JSON.parse(evt.data); } catch { return; }
    ch.subs.forEach((fn) => { try { fn(msg); } catch { /* noop */ } });
  };
  ws.onclose = () => {
    if (ch.ws !== ws) return;
    ch.ws = null;
    if (ch.closed || ch.subs.size === 0) return;
    const delay = Math.min(15000, 2000 * 2 ** Math.min(3, ch.attempt++));
    ch.timer = setTimeout(() => connect(ch, postId), delay);
  };
};

// subscribe(postId, onMessage) → unsubscribe()
export const subscribePost = (postId, onMessage) => {
  if (!postId || typeof onMessage !== "function") return () => {};
  let ch = channels.get(postId);
  if (!ch) {
    ch = { ws: null, subs: new Set(), timer: null, attempt: 0, closed: false };
    channels.set(postId, ch);
    connect(ch, postId);
  }
  ch.subs.add(onMessage);
  return () => {
    ch.subs.delete(onMessage);
    if (ch.subs.size > 0) return;
    ch.closed = true;
    clearTimeout(ch.timer);
    try { ch.ws?.close(); } catch { /* noop */ }
    channels.delete(postId);
  };
};

export const sendPost = (postId, obj) => {
  const ws = channels.get(postId)?.ws;
  if (ws?.readyState !== 1) return false;
  try { ws.send(JSON.stringify(obj)); return true; } catch { return false; }
};

export const postChannelOpen = (postId) => channels.get(postId)?.ws?.readyState === 1;

// Hook : abonne le composant tant qu'il est monté (handler toujours à jour, sans réabonnement).
export function usePostChannel(postId, handler, enabled = true) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!postId || !enabled) return undefined;
    return subscribePost(postId, (msg) => ref.current?.(msg));
  }, [postId, enabled]);
}
